import { useEffect, useMemo, useState } from "react";
import { Inbox, Mail, Trash2, Archive, Reply, CheckCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { EmptyState, Skeleton } from "@/components/admin/Field";
import ConfirmDialog from "@/components/admin/ConfirmDialog";

const STATUS_FILTERS = [
  { k: "all", l: "All" },
  { k: "new", l: "New" },
  { k: "read", l: "Read" },
  { k: "replied", l: "Replied" },
  { k: "archived", l: "Archived" },
];

const STATUS_COLOR = {
  new: "bg-[#E4FF00] text-black",
  read: "bg-white/10 text-white/85",
  replied: "bg-emerald-400/15 text-emerald-200",
  archived: "bg-white/[0.04] text-white/45",
};

function statusOf(m) {
  if (m?.status) return m.status;
  return m?.is_read ? "read" : "new";
}

export default function MessagesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [filter, setFilter] = useState("all");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/messages");
      setItems(data || []);
      if (data?.length && !selected) setSelected(data[0]);
    } catch {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((m) => statusOf(m) === filter);
  }, [items, filter]);

  async function setStatus(item, status) {
    try {
      const { data } = await api.patch(`/admin/messages/${item.id}`, { status });
      setItems((arr) => arr.map((m) => (m.id === item.id ? { ...m, ...data } : m)));
      if (selected?.id === item.id) setSelected({ ...item, ...data });
    } catch {
      toast.error("Update failed");
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/messages/${pendingDelete.id}`);
      setItems((arr) => arr.filter((m) => m.id !== pendingDelete.id));
      if (selected?.id === pendingDelete.id) setSelected(null);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setPendingDelete(null);
    }
  }

  const currentStatus = selected ? statusOf(selected) : null;

  return (
    <div className="px-6 py-8 md:px-10 md:py-12">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#0055ff]">Messages</span>
          <h1 className="mt-1 font-display text-3xl font-medium tracking-tight text-white md:text-4xl">Contact inbox</h1>
          <p className="mt-1 font-body text-sm text-white/50">Every contact form submission lives here.</p>
        </div>
        <div className="inline-flex flex-wrap items-center gap-1 rounded-full border border-white/10 bg-white/[0.02] p-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`rounded-full px-3.5 py-1.5 font-body text-xs transition ${
                filter === f.k ? "bg-white/[0.08] text-white" : "text-white/55 hover:text-white"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState icon={Inbox} title="No messages" description="Your inbox is quiet." />
          ) : (
            <ul className="max-h-[70vh] divide-y divide-white/[0.05] overflow-y-auto rounded-2xl border border-white/[0.07]">
              {filtered.map((m) => {
                const s = statusOf(m);
                return (
                  <li key={m.id}>
                    <button
                      onClick={() => {
                        setSelected(m);
                        if (s === "new") setStatus(m, "read");
                      }}
                      className={`flex w-full flex-col items-start gap-1 px-4 py-3 text-left transition ${
                        selected?.id === m.id ? "bg-white/[0.05]" : "hover:bg-white/[0.025]"
                      }`}
                    >
                      <div className="flex w-full items-center justify-between gap-2">
                        <span className="flex items-center gap-2 font-display text-sm text-white">
                          {s === "new" && <span className="h-1.5 w-1.5 rounded-full bg-[#E4FF00]" />}
                          {m.name}
                        </span>
                        <span className="font-mono text-[10px] text-white/35">
                          {new Date(m.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <span className="truncate font-body text-xs text-white/45">{m.email}</span>
                      <span className="line-clamp-1 font-body text-xs text-white/55">{m.message}</span>
                      <span
                        className={`mt-1 inline-flex rounded-full px-2 py-0.5 font-body text-[10px] uppercase tracking-[0.2em] ${STATUS_COLOR[s] || STATUS_COLOR.read}`}
                      >
                        {s}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <article className="rounded-3xl border border-white/[0.07] bg-white/[0.015] p-6 md:p-8">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-display text-2xl font-medium tracking-tight text-white">{selected.name}</h2>
                  <div className="mt-1 flex flex-wrap items-center gap-2 font-body text-xs text-white/55">
                    <a href={`mailto:${selected.email}`} className="flex items-center gap-1 hover:text-white">
                      <Mail className="h-3.5 w-3.5" /> {selected.email}
                    </a>
                    {selected.company && <span>· {selected.company}</span>}
                    {selected.project_type && <span>· {selected.project_type}</span>}
                    {selected.budget && <span>· {selected.budget}</span>}
                  </div>
                  {selected.source_page && (
                    <div className="mt-2 font-mono text-[11px] text-white/40">
                      Source: <span className="text-white/65">{selected.source_page}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 font-body text-[10px] uppercase tracking-[0.22em] ${STATUS_COLOR[currentStatus] || STATUS_COLOR.read}`}
                  >
                    {currentStatus}
                  </span>
                  {currentStatus === "new" && (
                    <button
                      onClick={() => setStatus(selected, "read")}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3.5 py-1.5 font-body text-xs text-white/85 hover:border-white/25"
                    >
                      <CheckCheck className="h-3.5 w-3.5" /> Mark read
                    </button>
                  )}
                  {currentStatus !== "new" && currentStatus !== "archived" && (
                    <button
                      onClick={() => setStatus(selected, "new")}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3.5 py-1.5 font-body text-xs text-white/85 hover:border-white/25"
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> Mark new
                    </button>
                  )}
                  {currentStatus !== "replied" && currentStatus !== "archived" && (
                    <button
                      onClick={() => setStatus(selected, "replied")}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3.5 py-1.5 font-body text-xs text-emerald-200 hover:border-emerald-300/40"
                    >
                      <Reply className="h-3.5 w-3.5" /> Mark replied
                    </button>
                  )}
                  {currentStatus !== "archived" ? (
                    <button
                      onClick={() => setStatus(selected, "archived")}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3.5 py-1.5 font-body text-xs text-white/85 hover:border-white/25"
                    >
                      <Archive className="h-3.5 w-3.5" /> Archive
                    </button>
                  ) : (
                    <button
                      onClick={() => setStatus(selected, "read")}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3.5 py-1.5 font-body text-xs text-white/85 hover:border-white/25"
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> Restore
                    </button>
                  )}
                  <button
                    onClick={() => setPendingDelete(selected)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3.5 py-1.5 font-body text-xs text-white/85 hover:border-red-400/40 hover:text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
              <div className="mt-6 whitespace-pre-wrap rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 font-body text-sm leading-relaxed text-white/80">
                {selected.message}
              </div>
              <div className="mt-4 font-mono text-[10px] text-white/35">
                Received {new Date(selected.created_at).toLocaleString()}
              </div>
            </article>
          ) : (
            <EmptyState icon={Inbox} title="Select a message" description="Pick a message from the list to view it." />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this message?"
        description={`The message from “${pendingDelete?.name || ""}” will be permanently removed.`}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
